import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { Sparkles, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePageMeta } from '../hooks/usePageMeta';

export const Login = () => {
  usePageMeta({
    title: 'Sign In — Epicurea Nexus',
    description: 'Sign in to your Epicurea Nexus consultant workspace.',
  });
  const { user, login } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to={location.state?.from || '/dashboard'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Check email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{
        backgroundImage: "url('https://static.prod-images.emergentagent.com/jobs/ea9cf972-3434-4165-bf74-a93c34e7a810/images/d8d3b06a78b6c2e2bf5c7a86e3da4f10f8c5e9e6c3b8b4f5a1c3d7e9f2a4b6c8.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: '#F9F8F6'
      }}
      data-testid="login-page"
    >
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/ea9cf972-3434-4165-bf74-a93c34e7a810/images/93589658ea558262a331193e658bbed9ba248c401b58657ed297db3b7130550f.png"
            alt="Epicurea Nexus"
            className="h-20 w-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-primary tracking-tight">Epicurea Nexus</h1>
          <p className="text-sm text-text-muted tracking-[0.2em] uppercase mt-1">Profit Recovery Specialists</p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-border rounded-md shadow-lg p-8">
          <div className="flex items-center space-x-2 mb-6">
            <LogIn className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold text-primary">Consultant Login</h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="admin@epicurea.com"
                required
                autoFocus
                data-testid="login-email"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-border rounded-md px-3 py-2 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                required
                data-testid="login-password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border-l-4 border-state-error rounded-md p-3 flex items-start space-x-2" data-testid="login-error">
                <AlertCircle className="h-4 w-4 text-state-error flex-shrink-0 mt-0.5" />
                <p className="text-sm text-state-error">{error}</p>
              </div>
            )}

            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-primary hover:bg-primary-hover text-white py-3"
              data-testid="login-submit"
            >
              {submitting ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="text-center flex items-center justify-center gap-4">
              <Link
                to="/forgot-password"
                className="text-xs text-primary font-semibold hover:underline"
                data-testid="login-forgot-link"
              >
                Forgot password?
              </Link>
              <span className="text-text-muted">·</span>
              <Link
                to="/signup"
                className="text-xs text-primary font-semibold hover:underline"
                data-testid="login-signup-link"
              >
                Sign up
              </Link>
            </div>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-border" />
            <span className="text-xs uppercase tracking-widest text-text-muted">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button
            type="button"
            onClick={() => {
              const redirect = `${window.location.origin}/auth/callback`;
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
            }}
            className="w-full flex items-center justify-center gap-3 border border-border rounded-md py-3 hover:bg-background transition"
            data-testid="login-google-btn"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.71H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
              <path d="M3.964 10.707A5.41 5.41 0 013.68 9c0-.593.102-1.17.284-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
              <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
            </svg>
            <span className="text-sm font-semibold">Sign in with Google</span>
          </button>

          <p className="text-xs text-text-muted text-center mt-6">
            Client access is invite-only — your operator must add your email first, then use <Link to="/signup" className="text-primary underline">Sign up</Link>.
          </p>
        </div>

        <p className="text-center text-xs text-text-muted mt-6">
          Powered by AI · Built for hospitality cost recovery
        </p>
      </div>
    </div>
  );
};
